import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { webhooks } from '@bb/contracts';
import { Badge, Button, Checkbox, ConfirmDialog, EmptyState, ErrorState, Field, Input, Select, StatusPill, Textarea } from '@bb/ui';
import type { WebhookEndpointInput, WebhookEndpointPatch, WebhookEndpointSummary, WebhookEndpointView, WebhookParam } from '../../api/client.ts';
import { formatWhen } from '../segments/SegmentsListPage.tsx';
import { webhookProblemText } from './webhookErrors.ts';

const ROUTE = '/env/$environmentId/settings/webhooks';
const endpointsKey = (environmentId: string) => ['webhook-endpoints', environmentId];

// ---------------------------------------------------------------------------------------------------------------
// Allowed domains

/** Domains typed one per line: lower-cased, blanks dropped; each checked like the API does. */
export function parseDomains(text: string): { domains: string[]; error?: string } {
  const domains = text
    .split(/[\s,]+/)
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  const bad = domains.find((d) => !webhooks.WebhookDomainPattern.safeParse(d).success);
  if (bad) return { domains, error: `${bad} is not a host name (optionally starting with *.).` };
  const twice = domains.find((d, i) => domains.indexOf(d) !== i);
  if (twice) return { domains, error: `${twice} is listed twice.` };
  if (domains.length > 100) return { domains, error: 'At most 100 domains.' };
  return { domains };
}

function AllowedDomains({ environmentId }: { environmentId: string }) {
  const { api } = useRouteContext({ from: ROUTE });
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['webhook-settings', environmentId], queryFn: () => api.webhookSettings(environmentId) });
  const [text, setText] = useState<string | null>(null);
  const stored = settings.data?.allowedDomains ?? [];
  const shown = text ?? stored.join('\n');
  const parsed = parseDomains(shown);
  const changed = text !== null && parsed.domains.join('\n') !== stored.join('\n');
  const save = useMutation({
    mutationFn: () => api.saveWebhookSettings(environmentId, { allowedDomains: parsed.domains }),
    onSuccess: (next) => {
      queryClient.setQueryData(['webhook-settings', environmentId], next);
      setText(null);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (changed && !parsed.error) save.mutate();
  };
  if (settings.isError) return <ErrorState title="Allowed domains could not be loaded" message={webhookProblemText(settings.error)} onRetry={() => void settings.refetch()} />;
  if (!settings.data) return <p aria-busy="true">Loading allowed domains…</p>;
  return (
    <form className="stack-form panel" aria-label="Allowed domains" onSubmit={submit}>
      <h2>Allowed domains</h2>
      <Field
        label="Domains (one per line)"
        hint="*.vcg.example allows every subdomain (not vcg.example itself); an exact host allows only that host. Checked when an endpoint is saved and before every send."
        error={parsed.error}
      >
        <Textarea rows={4} spellCheck={false} value={shown} placeholder={'*.vcg.example\nhooks.partner.example'} onChange={(e) => setText(e.target.value)} />
      </Field>
      {parsed.domains.length === 0 && (
        <p className="notice notice--warning" role="note">
          No domains yet: no endpoint can be saved until at least one is allowed.
        </p>
      )}
      {save.isError && <p role="alert">{webhookProblemText(save.error)}</p>}
      {save.isSuccess && !changed && <p role="status">Allowed domains saved.</p>}
      <div className="toolbar">
        <Button type="submit" variant="primary" disabled={!changed || Boolean(parsed.error)} loading={save.isPending}>
          Save domains
        </Button>
        {changed && <Button onClick={() => setText(null)}>Discard</Button>}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Endpoint form

interface Row {
  name: string;
  value: string;
}
interface ParamRow {
  name: string;
  type: WebhookParam['type'];
  required: boolean;
  description: string;
}
interface EndpointDraft {
  name: string;
  description: string;
  url: string;
  method: WebhookEndpointInput['method'];
  contentType: WebhookEndpointInput['contentType'];
  headers: Row[];
  secretHeaders: Row[];
  signed: boolean;
  signingSecretRef: string;
  params: ParamRow[];
  timeoutMs: string;
  maxAttempts: string;
  enabled: boolean;
}

const emptyDraft = (): EndpointDraft => ({
  name: '',
  description: '',
  url: 'https://',
  method: 'POST',
  contentType: 'application/json',
  headers: [],
  secretHeaders: [],
  signed: true,
  signingSecretRef: '',
  params: [],
  timeoutMs: String(webhooks.WEBHOOK_TIMEOUT_MS.default),
  maxAttempts: String(webhooks.WEBHOOK_MAX_ATTEMPTS.default),
  enabled: true,
});

const draftOf = (e: WebhookEndpointView): EndpointDraft => ({
  name: e.name,
  description: e.description ?? '',
  url: e.url,
  method: e.method,
  contentType: e.contentType,
  headers: e.headers.map((h) => ({ name: h.name, value: h.value })),
  secretHeaders: e.secretHeaders.map((h) => ({ name: h.name, value: h.secretRef })),
  signed: e.signingSecretRef !== null,
  signingSecretRef: e.signingSecretRef ?? '',
  params: e.params.map((p) => ({ name: p.name, type: p.type, required: p.required, description: p.description ?? '' })),
  timeoutMs: String(e.timeoutMs),
  maxAttempts: String(e.maxAttempts),
  enabled: e.enabled,
});

/** The request body of a draft: trimmed, empty optional parts left out, numbers as numbers. */
export function bodyOf(d: EndpointDraft): WebhookEndpointInput {
  return {
    name: d.name.trim(),
    description: d.description.trim() || null,
    url: d.url.trim(),
    method: d.method,
    contentType: d.contentType,
    headers: d.headers.map((h) => ({ name: h.name.trim(), value: h.value })),
    secretHeaders: d.secretHeaders.map((h) => ({ name: h.name.trim(), secretRef: h.value.trim() })),
    signingSecretRef: d.signed ? d.signingSecretRef.trim() : null,
    params: d.params.map((p) => ({ name: p.name.trim(), type: p.type, required: p.required, ...(p.description.trim() ? { description: p.description.trim() } : {}) })),
    timeoutMs: Number(d.timeoutMs),
    maxAttempts: Number(d.maxAttempts),
    enabled: d.enabled,
  };
}

/** Only what changed, for PATCH (the server checks the merged endpoint again and resolves only changed secrets). */
export function patchOf(before: WebhookEndpointInput, after: WebhookEndpointInput): WebhookEndpointPatch {
  return Object.fromEntries(Object.entries(after).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(before[key as keyof WebhookEndpointInput]))) as WebhookEndpointPatch;
}

/** The contract's rules as messages per path (`url`, `headers.0.name`, …). */
function issuesOf(body: WebhookEndpointInput): Record<string, string> {
  const parsed = webhooks.CreateWebhookEndpointBody.safeParse(body);
  const issues: Record<string, string> = {};
  if (parsed.success) return issues;
  for (const i of parsed.error.issues) {
    const path = i.path.join('.') || 'form';
    issues[path] ??= i.message === 'Invalid input' ? 'Invalid value.' : i.message;
  }
  if (body.signingSecretRef === '') issues.signingSecretRef = 'Enter the reference of the signing key, or turn signing off.';
  return issues;
}

const PARAM_TYPES = webhooks.WEBHOOK_PARAM_TYPES.map((t) => ({ value: t, label: t }));
const METHODS = webhooks.WEBHOOK_METHODS.map((m) => ({ value: m, label: m }));
const CONTENT_TYPES = webhooks.WEBHOOK_CONTENT_TYPES.map((t) => ({ value: t, label: t }));

function RowError({ message }: { message: string | undefined }) {
  return message ? <span className="bb-field__error">{message}</span> : null;
}

function EndpointForm({ environmentId, editing, onDone }: { environmentId: string; editing: WebhookEndpointView | null; onDone: () => void }) {
  const { api } = useRouteContext({ from: ROUTE });
  const queryClient = useQueryClient();
  const [d, setD] = useState<EndpointDraft>(() => (editing ? draftOf(editing) : emptyDraft()));
  const [touched, setTouched] = useState(false);
  const set = (patch: Partial<EndpointDraft>) => setD((was) => ({ ...was, ...patch }));
  const body = bodyOf(d);
  const issues = issuesOf(body);
  const invalid = Object.keys(issues).length > 0;
  const shown = (path: string) => (touched ? issues[path] : undefined);
  const save = useMutation({
    mutationFn: () => (editing ? api.updateWebhookEndpoint(environmentId, editing.id, patchOf(bodyOf(draftOf(editing)), body)) : api.createWebhookEndpoint(environmentId, body)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: endpointsKey(environmentId) });
      onDone();
    },
  });
  const unchanged = editing !== null && Object.keys(patchOf(bodyOf(draftOf(editing)), body)).length === 0;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!invalid && !unchanged) save.mutate();
  };
  const title = editing ? `Edit ${editing.name}` : 'New endpoint';
  const editRows = (key: 'headers' | 'secretHeaders', index: number, patch: Partial<Row> | null) =>
    set({ [key]: patch === null ? d[key].filter((_, i) => i !== index) : d[key].map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  const editParam = (index: number, patch: Partial<ParamRow> | null) => set({ params: patch === null ? d.params.filter((_, i) => i !== index) : d.params.map((p, i) => (i === index ? { ...p, ...patch } : p)) });

  return (
    <form className="stack-form panel" aria-label={title} onSubmit={submit} noValidate>
      <h2>{title}</h2>
      <Field label="Name" error={shown('name')}>
        <Input value={d.name} maxLength={100} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field label="Description (optional)">
        <Input value={d.description} maxLength={500} onChange={(e) => set({ description: e.target.value })} />
      </Field>
      <Field label="URL" hint="https:// and a host from the allowed domains. Liquid only after the host, reading declared params: /users/{{ params.user_id }}." error={shown('url')}>
        <Input value={d.url} spellCheck={false} onChange={(e) => set({ url: e.target.value })} />
      </Field>
      <div className="inline-form">
        <Field label="Method">
          <Select options={METHODS} value={d.method} onChange={(e) => set({ method: e.target.value as EndpointDraft['method'] })} />
        </Field>
        <Field label="Body template content type" hint="Without a body template the body is always the params as JSON.">
          <Select options={CONTENT_TYPES} value={d.contentType} onChange={(e) => set({ contentType: e.target.value as EndpointDraft['contentType'] })} />
        </Field>
      </div>
      <div className="inline-form">
        <Field label="Timeout (ms)" hint="1,000–10,000." error={shown('timeoutMs')}>
          <Input type="number" min={webhooks.WEBHOOK_TIMEOUT_MS.min} max={webhooks.WEBHOOK_TIMEOUT_MS.max} step={500} value={d.timeoutMs} onChange={(e) => set({ timeoutMs: e.target.value })} />
        </Field>
        <Field label="Max attempts" hint="1–5, the first included; 408, 429 and 5xx are retried." error={shown('maxAttempts')}>
          <Input type="number" min={webhooks.WEBHOOK_MAX_ATTEMPTS.min} max={webhooks.WEBHOOK_MAX_ATTEMPTS.max} value={d.maxAttempts} onChange={(e) => set({ maxAttempts: e.target.value })} />
        </Field>
      </div>
      <Checkbox label="Enabled" checked={d.enabled} onCheckedChange={(enabled) => set({ enabled })} />

      <fieldset>
        <legend>Signature</legend>
        <Checkbox label="Sign requests (X-BB-Signature)" checked={d.signed} onCheckedChange={(signed) => set({ signed })} />
        {d.signed && (
          <Field label="Signing secret reference" hint="HMAC-SHA256 key: env:NAME (a variable of the service) or file:/path (inside its secrets directory)." error={shown('signingSecretRef')}>
            <Input value={d.signingSecretRef} spellCheck={false} placeholder="env:BB_WEBHOOK_SIGNING_KEY" onChange={(e) => set({ signingSecretRef: e.target.value })} />
          </Field>
        )}
        <p className="hint">Secrets are references only: their values are set on the server, never here, and are never shown.</p>
      </fieldset>

      <fieldset>
        <legend>Headers</legend>
        <p className="hint">Plain headers; values may use Liquid. Credentials (Authorization, Cookie, *token*, *api-key*, *secret*) go to secret headers.</p>
        {d.headers.map((h, i) => (
          <div key={i} className="inline-form" role="group" aria-label={`Header ${i + 1}`}>
            <Input aria-label={`Header ${i + 1} name`} value={h.name} spellCheck={false} onChange={(e) => editRows('headers', i, { name: e.target.value })} />
            <Input aria-label={`Header ${i + 1} value`} value={h.value} spellCheck={false} onChange={(e) => editRows('headers', i, { value: e.target.value })} />
            <Button size="sm" variant="ghost" onClick={() => editRows('headers', i, null)}>
              Remove header {i + 1}
            </Button>
            <RowError message={shown(`headers.${i}.name`) ?? shown(`headers.${i}.value`)} />
          </div>
        ))}
        <Button size="sm" disabled={d.headers.length >= 20} onClick={() => set({ headers: [...d.headers, { name: '', value: '' }] })}>
          Add header
        </Button>
      </fieldset>

      <fieldset>
        <legend>Secret headers</legend>
        <p className="hint">The value comes from a secret reference when the request is sent, e.g. Authorization ← env:BB_PARTNER_TOKEN.</p>
        {d.secretHeaders.map((h, i) => (
          <div key={i} className="inline-form" role="group" aria-label={`Secret header ${i + 1}`}>
            <Input aria-label={`Secret header ${i + 1} name`} value={h.name} spellCheck={false} onChange={(e) => editRows('secretHeaders', i, { name: e.target.value })} />
            <Input aria-label={`Secret header ${i + 1} reference`} value={h.value} spellCheck={false} placeholder="env:NAME" onChange={(e) => editRows('secretHeaders', i, { value: e.target.value })} />
            <Button size="sm" variant="ghost" onClick={() => editRows('secretHeaders', i, null)}>
              Remove secret header {i + 1}
            </Button>
            <RowError message={shown(`secretHeaders.${i}.name`) ?? shown(`secretHeaders.${i}.secretRef`)} />
          </div>
        ))}
        <Button size="sm" disabled={d.secretHeaders.length >= 10} onClick={() => set({ secretHeaders: [...d.secretHeaders, { name: '', value: '' }] })}>
          Add secret header
        </Button>
      </fieldset>

      <fieldset>
        <legend>Params</legend>
        <p className="hint">What a message fills with Liquid; converted to the type at send time. Without a body template they are the JSON body.</p>
        {d.params.map((p, i) => (
          <div key={i} className="inline-form" role="group" aria-label={`Param ${i + 1}`}>
            <Input aria-label={`Param ${i + 1} name`} value={p.name} spellCheck={false} onChange={(e) => editParam(i, { name: e.target.value })} />
            <Select aria-label={`Param ${i + 1} type`} options={PARAM_TYPES} value={p.type} onChange={(e) => editParam(i, { type: e.target.value as ParamRow['type'] })} />
            <Checkbox label={`Param ${i + 1} required`} checked={p.required} onCheckedChange={(required) => editParam(i, { required })} />
            <Input aria-label={`Param ${i + 1} description`} value={p.description} maxLength={300} placeholder="Description (optional)" onChange={(e) => editParam(i, { description: e.target.value })} />
            <Button size="sm" variant="ghost" onClick={() => editParam(i, null)}>
              Remove param {i + 1}
            </Button>
            <RowError message={shown(`params.${i}.name`)} />
          </div>
        ))}
        <Button size="sm" disabled={d.params.length >= 50} onClick={() => set({ params: [...d.params, { name: '', type: 'string', required: false, description: '' }] })}>
          Add param
        </Button>
      </fieldset>

      {touched && invalid && <p role="alert">Fix the marked fields first.</p>}
      {save.isError && <p role="alert">{webhookProblemText(save.error)}</p>}
      <div className="toolbar">
        <Button type="submit" variant="primary" loading={save.isPending} disabled={unchanged}>
          {editing ? 'Save endpoint' : 'Create endpoint'}
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Endpoint list

const isView = (e: WebhookEndpointView | WebhookEndpointSummary): e is WebhookEndpointView => 'url' in e;

function EndpointCard({ environmentId, endpoint: e, onEdit }: { environmentId: string; endpoint: WebhookEndpointView; onEdit: () => void }) {
  const { api } = useRouteContext({ from: ROUTE });
  const queryClient = useQueryClient();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: endpointsKey(environmentId) });
  const toggle = useMutation({ mutationFn: () => api.updateWebhookEndpoint(environmentId, e.id, { enabled: !e.enabled }), onSuccess: refresh });
  const archive = useMutation({ mutationFn: () => api.archiveWebhookEndpoint(environmentId, e.id), onSuccess: refresh });
  return (
    <article className="panel" aria-label={`Endpoint ${e.name}`}>
      <h3>
        {e.name} {e.enabled ? <StatusPill tone="success">Enabled</StatusPill> : <StatusPill tone="neutral">Disabled</StatusPill>}{' '}
        {e.signingSecretRef ? <StatusPill tone="info">Signed</StatusPill> : <StatusPill tone="warning">Not signed</StatusPill>}
      </h3>
      {e.description && <p className="hint">{e.description}</p>}
      <p>
        <code>
          {e.method} {e.url}
        </code>
      </p>
      <dl className="facts">
        <dt>Body template type</dt>
        <dd>{e.contentType}</dd>
        <dt>Timeout · attempts</dt>
        <dd>
          {e.timeoutMs.toLocaleString('en-US')} ms · up to {e.maxAttempts}
        </dd>
        <dt>Params</dt>
        <dd>
          {e.params.length
            ? e.params.map((p) => (
                <span key={p.name} className="webhook-param">
                  <code>{p.name}</code> <Badge>{p.type}</Badge>
                  {p.required && <Badge tone="warning">required</Badge>}{' '}
                </span>
              ))
            : 'None'}
        </dd>
        <dt>Headers</dt>
        <dd>{e.headers.map((h) => h.name).join(', ') || 'None'}</dd>
        <dt>Secret headers</dt>
        <dd>{e.secretHeaders.map((h) => `${h.name} ← ${h.secretRef}`).join(', ') || 'None'}</dd>
        <dt>Signing secret</dt>
        <dd>{e.signingSecretRef ?? 'Not signed'}</dd>
        <dt>Updated</dt>
        <dd>
          {formatWhen(e.updatedAt)}
          {e.updatedBy ? ` by ${e.updatedBy}` : ''}
        </dd>
      </dl>
      <div className="toolbar">
        <Button onClick={onEdit}>Edit</Button>
        <Button onClick={() => toggle.mutate()} loading={toggle.isPending}>
          {e.enabled ? 'Disable' : 'Enable'}
        </Button>
        <Button variant="danger" onClick={() => setConfirmArchive(true)}>
          Archive
        </Button>
      </div>
      {(toggle.isError || archive.isError) && <p role="alert">{webhookProblemText(toggle.error ?? archive.error)}</p>}
      <ConfirmDialog open={confirmArchive} onOpenChange={setConfirmArchive} title={`Archive ${e.name}?`} confirmLabel="Archive endpoint" onConfirm={() => archive.mutate()} busy={archive.isPending}>
        <p>Messages that call it stop sending (webhook_endpoint_missing) until they pick another endpoint. The name becomes free.</p>
      </ConfirmDialog>
    </article>
  );
}

/**
 * Settings → Webhooks (BB-142): the domains this environment's endpoints may call, and the registered endpoints —
 * URL template, method, headers, secret headers and the signing key (references only), typed params, timeout and
 * attempts. Messages pick one of these endpoints and fill its params; they never carry a URL. Needs integrations.manage.
 */
export function WebhookEndpointsPage() {
  const { environmentId } = useParams({ from: ROUTE });
  const { api } = useRouteContext({ from: ROUTE });
  const [form, setForm] = useState<{ editing: WebhookEndpointView | null } | null>(null);
  const list = useQuery({ queryKey: endpointsKey(environmentId), queryFn: () => api.listWebhookEndpoints(environmentId) });
  const items = (list.data ?? []).filter(isView);
  useEffect(() => setForm(null), [environmentId]);
  return (
    <section>
      <h1>Webhooks</h1>
      <p className="hint">Campaigns and Canvas Message steps call only the endpoints registered here, on allowed https domains, with a signature and an idempotency key (X-BB-Idempotency-Key).</p>
      <AllowedDomains environmentId={environmentId} />
      <h2>Endpoints</h2>
      {!form && (
        <Button variant="primary" onClick={() => setForm({ editing: null })}>
          New endpoint
        </Button>
      )}
      {form && <EndpointForm key={form.editing?.id ?? 'new'} environmentId={environmentId} editing={form.editing} onDone={() => setForm(null)} />}
      {list.isError ? (
        <ErrorState title="Endpoints could not be loaded" message={webhookProblemText(list.error)} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <p aria-busy="true">Loading endpoints…</p>
      ) : items.length === 0 ? (
        <EmptyState title="No endpoints yet" description="Register an endpoint so messages can call it." />
      ) : (
        <ul aria-label="Endpoints" className="plain-list">
          {items.map((e) => (
            <li key={e.id}>
              <EndpointCard environmentId={environmentId} endpoint={e} onEdit={() => setForm({ editing: e })} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
